import React, { useCallback, useEffect, useMemo } from 'react';
import { Platform, Pressable, View } from 'react-native';
import WorkSitePublish from '@/components/Coworker/WorkSitePublish';
import HtmlPlayablePreview from '@/components/Coworker/HtmlPlayablePreview';
import { filesApi } from '@/lib/api/files';
import { openMessageFile } from '@/constants/utils/files';
import { suggestedHtmlFilename } from '@/lib/workSitePublishing';
import useHtmlPreviewHistory from '@/hooks/useHtmlPreviewHistory';
import Text from '@/components/Typography/Text';

// A fenced HTML response has no File yet. Create one only when the user opens
// or publishes the artifact, so all actions use the same saved source.
export default function InlineHtmlPreview({ html: currentHtml, conversationId, isFromTemporaryChat = false, messageId, userId, canPersist = false, onFollowUp, initialCode = false }) {
  const context = { userId, conversationId, messageId, canPersist: canPersist && !isFromTemporaryChat, onFollowUp };
  const history = useHtmlPreviewHistory(currentHtml, context);
  const html = history.html;
  const previewKey = useMemo(() => JSON.stringify([userId, conversationId, messageId, html]), [userId, conversationId, messageId, html]);
  const name = useMemo(() => suggestedHtmlFilename(html), [html]);
  const source = useMemo(() => ({ active: true, file: null }), [html, conversationId, isFromTemporaryChat, userId, messageId]);
  useEffect(() => {
    source.active = true;
    return () => { source.active = false; };
  }, [source]);

  const saveArtifact = useCallback(async () => {
    if (!source.file) {
      let file;
      let cleanup;
      try {
        if (Platform.OS === 'web') {
          file = new File([html], name, { type: 'text/html' });
        } else {
          const FileSystem = await import('expo-file-system');
          const uri = `${FileSystem.cacheDirectory}html-artifact-${Date.now()}-${Math.random().toString(36).slice(2)}.html`;
          cleanup = () => FileSystem.deleteAsync(uri, { idempotent: true });
          await FileSystem.writeAsStringAsync(uri, html);
          file = { uri, name, type: 'text/html' };
        }
        const uploaded = await filesApi.uploadFile({ file, name, conversationId, isFromTemporaryChat });
        if (!uploaded?.file?.cdnPath) throw new Error('HTML artifact was not saved');
        source.file = { ...uploaded.file, conversationId };
      } finally {
        if (cleanup) await cleanup().catch(() => {});
      }
    }
    return source.file;
  }, [html, name, conversationId, isFromTemporaryChat, source]);

  // Opening and publishing share the same saved File, even if both surfaces
  // are clicked while its initial upload is still in flight.
  const prepareFile = useCallback(async () => {
    if (!source.active) throw new Error('This preview changed. Open the current creation to publish it.');
    if (!source.pending) source.pending = saveArtifact().finally(() => { source.pending = null; });
    const file = await source.pending;
    if (!source.active) throw new Error('This preview changed. Open the current creation to publish it.');
    return file;
  }, [source, saveArtifact]);
  const openArtifact = useCallback(async () => {
    try {
      const file = await prepareFile();
      if (source.active) openMessageFile(file);
    } catch (error) {
      if (source.active) throw error;
    }
  }, [prepareFile, source]);

  return <View style={{ gap: 8 }}>
    {history.versions.length > 1 && <View accessibilityLabel="Preview versions" style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
      {history.versions.map((version, index) => <Pressable key={version.id} accessibilityRole="button" accessibilityState={{ selected: version.id === history.selectedId }}
        onPress={() => history.select(version.id)} style={{ minHeight: 36, paddingVertical: 8, paddingHorizontal: 4 }}>
        <Text size="xs" color={version.id === history.selectedId ? 'primary' : 'secondary'}>{index === 0 ? 'Current version' : `Earlier version ${index}`}</Text>
      </Pressable>)}
    </View>}
    <HtmlPlayablePreview key={JSON.stringify([userId, conversationId, messageId])} name={name} initialCode={initialCode} sourceKey={previewKey} loadHtml={() => html} onExpand={openArtifact} expandLabel="Open artifact" interactionContext={{ ...context, title: name }} />
    <WorkSitePublish key={`${previewKey}:${isFromTemporaryChat}`} prepareFile={prepareFile} file={{ name }} />
  </View>;
}
